import { TypedDocumentNode } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { call, CallEffect, takeEvery } from 'redux-saga/effects';

import { action } from 'typesafe-actions';
import { request } from 'utils/apollo';

enum RootActionTypes {
  SAGA_QUERY = 'app/root/SAGA_QUERY',
  SAGA_MUTATION = 'app/root/SAGA_MUTATION',
}

export interface UseApiSagaOptions<TData, TVariables> extends ApiCallbacks<TData> {
  variables?: TVariables;
  authentication?: boolean;
  errorPolicy?: 'none' | 'ignore' | 'all';
}

export interface ApiState<TData> {
  data: TData | null;
  loading: boolean;
  error: any;
}

export interface ApiCallbacks<TData> {
  onSuccess?: (data: TData) => void;
  onError?: (error: any) => void;
}

interface SagaCallPayload<TData = any, TVariables = any> extends ApiCallbacks<TData> {
  gqlQueryMutate?: DocumentNode | TypedDocumentNode<TData, TVariables>;
  variables?: TVariables;
  authentication?: boolean;
  errorPolicy?: 'none' | 'ignore' | 'all';
}

export type SagaGeneratorFn<TData, TVariables> = (variables?: TVariables) => Generator<CallEffect<TData>, TData, TData>;

export const callApiQuery = <TData = any, TVariables = any>(payload: SagaCallPayload<TData, TVariables>) =>
  action(RootActionTypes.SAGA_QUERY, payload);

export const callApiMutation = <TData = any, TVariables = any>(payload: SagaCallPayload<TData, TVariables>) =>
  action(RootActionTypes.SAGA_MUTATION, payload);

function* handleSagaQuery(action: ReturnType<typeof callApiQuery>) {
  const { gqlQueryMutate, variables, authentication, errorPolicy, onSuccess, onError } = action.payload;
  try {
    const response = yield call(
      request,
      { variables: variables, query: gqlQueryMutate, errorPolicy: errorPolicy },
      authentication,
    );
    if (!response?.errors) {
      onSuccess?.(response);
    } else {
      onError?.(response.errors);
    }
  } catch (err) {
    onError?.(err);
  }
}

function* handleSagaMutation(action: ReturnType<typeof callApiMutation>) {
  const { gqlQueryMutate, variables, authentication, onSuccess, onError } = action.payload;
  try {
    const response = yield call(request, { variables: variables, mutation: gqlQueryMutate, authentication: authentication });
    if (!response?.errors) {
      onSuccess?.(response);
    } else {
      onError?.(response.errors);
    }
  } catch (err) {
    onError?.(err);
  }
}

export default function* rootSaga() {
  yield takeEvery(RootActionTypes.SAGA_QUERY, handleSagaQuery);
  yield takeEvery(RootActionTypes.SAGA_MUTATION, handleSagaMutation);
}
